// Configuration for tenant extraction strategies
export interface TenantExtractionConfig {
  markerPart: string;
  strategy: 'before-marker' | 'fixed-position';
  position?: number; // for fixed-position strategy
}

const DEFAULT_CONFIG: TenantExtractionConfig = {
  markerPart: 'esrpapplication',
  strategy: 'before-marker'
};

/**
 * Gets hostname from URL or current location
 */
const getHostname = (url?: string): string => {
  if (url) {
    try {
      return new URL(url).hostname;
    } catch {
      return url; // assume it's already a hostname
    }
  }
  
  return typeof window !== 'undefined' ? window.location.hostname : '';
};

/**
 * Splits hostname into domain parts
 */
const parseDomainParts = (hostname: string): string[] => {
  return hostname.split('.');
};

/**
 * Extracts tenant ID using before-marker strategy
 */
const extractTenantByMarker = (parts: string[], markerPart: string): string | null => {
  const markerIndex = parts.findIndex(part => part === markerPart);
  return markerIndex > 0 ? parts[markerIndex - 1] : null;
};

/**
 * Extracts tenant ID using fixed-position strategy
 */
const extractTenantByPosition = (parts: string[], position: number): string | null => {
  const index = parts.length - position;
  return index >= 0 && index < parts.length ? parts[index] : null;
};

/**
 * Extracts tenant ID from URL using configurable strategy
 * 
 * @param url - URL or hostname for analysis (optional, defaults to window.location.hostname)
 * @param config - extraction configuration (optional, uses default config)
 * @returns tenant ID or null if not found
 */
export const getTenantFromSubdomain = (
  url?: string, 
  config: TenantExtractionConfig = DEFAULT_CONFIG
): string | null => {
  try {
    // FOR LOCALHOST TESTING ONLY
    if (process.env.NODE_ENV !== 'production') {
      return "etrackson";
    }

    const hostname = getHostname(url);
    
    if (!hostname) {
      return null;
    }

    const parts = parseDomainParts(hostname);
    
    switch (config.strategy) {
      case 'before-marker':
        return extractTenantByMarker(parts, config.markerPart);
      
      case 'fixed-position':
        if (config.position === undefined) {
          throw new Error('Position is required for fixed-position strategy');
        }
        return extractTenantByPosition(parts, config.position);
      
      default:
        return null;
    }
  } catch (error) {
    console.error('Error extracting tenant from subdomain:', error);
    return null;
  }
};

/**
 * Creates tenant header object
 */
const createTenantHeader = (tenantId: string): Record<string, string> => {
  return { 'X-Tenant-ID': tenantId };
};

/**
 * Creates authorization header object
 */
const createAuthHeader = (token: string): Record<string, string> => {
  return { Authorization: `Bearer ${token}` };
};

/**
 * Merges multiple header objects
 */
const mergeHeaders = (...headerObjects: Record<string, string>[]): Record<string, string> => {
  return headerObjects.reduce((merged, headers) => ({ ...merged, ...headers }), {});
};

/**
 * Creates headers object with automatically added X-Tenant-ID
 * 
 * @param additionalHeaders - additional headers to include
 * @param tenantId - optional tenant ID (if not provided, extracted from URL)
 * @returns headers object
 */
export const getTenantHeaders = (
  additionalHeaders: Record<string, string> = {},
  tenantId?: string
): Record<string, string> => {
  const extractedTenantId = tenantId || getTenantFromSubdomain();
  
  const headers = [additionalHeaders];
  
  if (extractedTenantId) {
    headers.push(createTenantHeader(extractedTenantId));
  }

  return mergeHeaders(...headers);
};

/**
 * Creates headers with authorization only
 * 
 * @param token - authorization token
 * @param additionalHeaders - additional headers
 * @returns headers object with Authorization
 */
export const getAuthHeaders = (
  token: string,
  additionalHeaders: Record<string, string> = {}
): Record<string, string> => {
  return mergeHeaders(createAuthHeader(token), additionalHeaders);
};

/**
 * Creates headers with authorization and tenant ID
 * 
 * @param token - authorization token
 * @param additionalHeaders - additional headers
 * @param tenantId - optional tenant ID (if not provided, extracted from URL)
 * @returns headers object with Authorization and X-Tenant-ID
 */
export const getAuthWithTenantHeaders = (
  token: string,
  additionalHeaders: Record<string, string> = {},
  tenantId?: string
): Record<string, string> => {
  const extractedTenantId = tenantId || getTenantFromSubdomain();
  
  const headers = [
    createAuthHeader(token),
    additionalHeaders
  ];
  
  if (extractedTenantId) {
    headers.push(createTenantHeader(extractedTenantId));
  }
  
  return mergeHeaders(...headers);
};